import { CenterContainer } from 'components/common/CenterContainer'
import Image from 'next/image'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Footer from 'components/Footer'
import { NextSeo } from 'next-seo'
import EffectiveNav from 'components/Nav/EffectiveNav'
import { isValidEmail } from 'lib/helpers'
import Button from 'components/Button'
import { TDataSubmitType } from './api/vaultDaoContact'
import { faArrowDown, faCheck } from '@fortawesome/pro-solid-svg-icons'
import { StyledSelect } from 'components/common/Select'
import useRecaptcha from 'hooks/useRecaptcha'
import MFDAnnouncementBanner from 'components/Collectives/Common/MFDAnnouncementBanner'

const divizer = 3

const metaImage =
  'https://i.upstreamapp.com/ogImages/collectives/Vault%20Open%20Graph%20Image.png'
const metaImageAlt = `Secure your crypto assets using Vault DAO`
const metaUrl = 'https://upstreamapp.com/vault'

type TValueType = {
  label: string
  value: string
}
const NFTValueOptions: TValueType[] = [
  { label: 'Less than $10,000', value: 'Less than $10,000' },
  { label: '$10,000-$100,000', value: '$10,000-$100,000' },
  { label: '$100,000-$1M', value: '$100,000-$1M' },
  { label: 'More than $1M', value: 'More than $1M' },
  { label: `I’d prefer not to share`, value: `I’d prefer not to share` }
]

const pageWidth = 1440

function CollectivesPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [avgValueNFT, setAvgValueNFt] = useState('')
  const [otherQuestion, setOtherQuestions] = useState('')

  const [clickedSend, setClickedSend] = useState(false)
  const [saving, setSaving] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const [trackedFormStart, setTrackedFormStart] = useState(false)
  useEffect(() => {
    if (
      trackedFormStart ||
      (!name && !email && !avgValueNFT && !otherQuestion)
    ) {
      return
    }

    window.analytics.trackAll('VaultDAO Form: Started', {
      email
    })
    setTrackedFormStart(true)
  }, [trackedFormStart, name, email, avgValueNFT, otherQuestion])

  const handleRequestAccessClick = () => {
    window.analytics.trackAll('VaultDAO Form: Clicked Request Access', {
      email
    })
    formContainerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
    setTimeout(() => nameRef.current?.focus(), 750)
  }
  const handleEmailBlur = () => {
    if (isValidEmail(email)) {
      window.analytics.trackAll('VaultDAO Form: Provided Email', {
        email
      })
    }
  }

  const errored = useMemo(
    () => !avgValueNFT.length || !email.length || !isValidEmail(email),
    [avgValueNFT.length, email]
  )
  const emailError = useMemo(() => !isValidEmail(email), [email])
  const nameRef = useRef<HTMLInputElement>(null)

  const submit = useCallback(
    async recaptchaToken => {
      setSaving(true)

      const data: TDataSubmitType = {
        name,
        email,
        avgValueNFT,
        otherQuestion,
        recaptchaToken
      }

      const resp = await fetch('/api/vaultDaoContact', {
        method: 'POST',
        body: JSON.stringify(data)
      }).then(x => x.json())

      window.analytics.trackAll('VaultDAO Form: Form Submitted')

      if (resp.success) {
        setSubmitted(true)
        setSaving(false)
      } else {
        setSubmitted(false)
        setSaving(false)
      }
    },
    [avgValueNFT, email, name, otherQuestion]
  )

  const onFormSubmit = useCallback(
    (recaptchaToken: string) => {
      setClickedSend(true)
      if (errored) {
        return
      }
      submit(recaptchaToken)
    },
    [errored, submit]
  )

  const { handleReCaptchaVerify } = useRecaptcha(onFormSubmit, 'vaultSubmit')

  const formContainerRef = useRef<HTMLDivElement>(null!)

  return (
    <>
      <NextSeo
        openGraph={{
          url: metaUrl,
          images: [
            {
              url: metaImage,
              width: 1200,
              height: 630,
              alt: metaImageAlt
            }
          ]
        }}
        twitter={{
          cardType: 'summary_large_image'
        }}
        additionalMetaTags={[
          {
            name: 'twitter:image',
            content: metaImage
          },
          {
            name: 'twitter:image:alt',
            content: 'DAOs Introduction Flyer'
          }
        ]}
      />

      <MFDAnnouncementBanner bannerStyle="grey" />

      <EffectiveNav />

      <div className="w-full -mt-24">
        <div className="landing-page-gradient-box  min-h-[72rem] md:min-h-fit">
          <div
            className="mx-8 pt-10 flex flex-1 flex-col items-center md:items-start md:justify-center md:flex-row-reverse "
            style={{ maxWidth: pageWidth }}
          >
            <div className="ml-20 md:ml-32 lg:ml-80">
              <Image
                src="/static/vaultDao/vaultDaoHero.png"
                width={991 / divizer}
                height={1388 / divizer}
                alt="Upstream DAO Hero"
              />
            </div>

            <div className="w-96 mt-12 sm:mt-10 text-center sm:text-left">
              <span className="font-black text-7xl leading-5">Vault DAO</span>

              <div className="mt-4">
                <h1 className="font-nunito text-[22px]">
                  Keep your cryptocurrency and NFTs safe with Upstream’s Vault
                  DAO.
                </h1>
                <br />
                <h2 className="font-nunito text-[22px]">
                  Set up your personal Vault to protect your assets from scams
                  and hacking.
                </h2>
                <br />
                <p className="font-nunito text-[22px]">Interested?</p>
              </div>
              <div className="mt-8">
                <Button
                  icon={faArrowDown}
                  label="Request Early Access"
                  color="white"
                  size="xl"
                  className="w-full border-2 rounded-xl border-blue-500 w-84 py-5"
                  iconClassName="ml-6 text-blue font-bold"
                  labelClassName="ml-2 mr-8 text-blue text-lg font-bold"
                  onClick={handleRequestAccessClick}
                />
              </div>
            </div>
          </div>
        </div>

        <CenterContainer>
          <div
            ref={formContainerRef}
            className="px-4 w-full flex justify-center"
          >
            <div className="py-4 px-8 my-24 border border-gray-200 rounded-lg shadow-md w-full sm:max-w-xl">
              <div className="py-4">
                <span className="font-black text-[40px]">
                  Request Early Access
                </span>
              </div>

              <div className="py-3">
                <label
                  htmlFor="name"
                  className="block font-medium text-gray-800"
                >
                  Name
                  <span className="text-red-500"> *</span>
                </label>

                <input
                  type="text"
                  name="name"
                  id="name"
                  placeholder="Enter your name"
                  className="mt-2 border border-gray-300 rounded-md block w-full px-2 text-gray-800 placeholder-gray-400 focus:ring-0 sm:text-sm py-2"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  ref={nameRef}
                />

                {clickedSend && !name.length && (
                  <span className="text-red-500 text-xs">Required *</span>
                )}
              </div>

              <div className="py-3">
                <label
                  htmlFor="email"
                  className="block font-medium text-gray-800"
                >
                  Email
                  <span className="text-red-500"> *</span>
                </label>

                <input
                  type="text"
                  name="email"
                  id="email"
                  placeholder="you@domain.com"
                  className=" mt-2 border border-gray-300 rounded-md block w-full px-2 text-gray-800 placeholder-gray-400 focus:ring-0 sm:text-sm py-2"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onBlur={handleEmailBlur}
                />
                {clickedSend && (
                  <>
                    {!email.length && (
                      <span className="text-red-500 text-xs">Required *</span>
                    )}
                    {!!email.length && emailError && (
                      <span className="text-red-500 text-xs">
                        Wrong Email Format
                      </span>
                    )}
                  </>
                )}
              </div>

              <div className="py-3">
                <label
                  htmlFor="avgValueNFT"
                  className="block font-medium text-gray-800"
                >
                  What is the value of NFTs and tokens you’d like to put in your
                  Vault?<span className="text-red-500">*</span>
                </label>

                <StyledSelect<TValueType, false>
                  options={NFTValueOptions}
                  value={NFTValueOptions.find(val => val.value === avgValueNFT)}
                  onChange={v => {
                    if (v) {
                      setAvgValueNFt(v?.value)
                    }
                  }}
                  placeholder="Select range"
                />

                {clickedSend && !avgValueNFT.length && (
                  <span className="text-red-500 text-xs">Required *</span>
                )}
              </div>

              <div className="py-3">
                <label
                  htmlFor="otherQuestion"
                  className="block font-medium text-gray-800"
                >
                  Share any questions or additional information you’d like us to
                  know.
                </label>

                <textarea
                  name="otherQuestion"
                  id="otherQuestion"
                  className="mt-2 border border-gray-300 rounded-md block w-full px-2 text-gray-800 placeholder-gray-400 focus:ring-0 sm:text-sm py-2"
                  value={otherQuestion}
                  onChange={e => setOtherQuestions(e.target.value)}
                />
              </div>

              <div className="mt-8">
                <Button
                  disabled={saving || submitted}
                  loading={saving}
                  color="blue"
                  label={submitted ? 'Submitted' : 'Submit'}
                  icon={submitted ? faCheck : undefined}
                  size="xl"
                  onClick={handleReCaptchaVerify}
                  className="w-full mb-10"
                />
              </div>
            </div>
          </div>
        </CenterContainer>
      </div>

      <Footer />
    </>
  )
}

export default CollectivesPage
